import React from "react";
import { Button } from "primereact/button";
import type { ThgStationRow } from "../../../types/thgAnnualSummary";
import type { PayoutBusy } from "../hooks/useThgPayoutActions";
import { PAYOUT_COPY } from "../ThgAnnualSummary.consts";
import { isUnpaidInternal } from "../utils/thgInternal";

export interface PayoutRowActions {
  busy: PayoutBusy | null;
  /** True while anything else that changes the year runs (a recompute): no credit-note action may start. */
  locked: boolean;
  onGenerate: (row: ThgStationRow) => void;
  onPdf: (row: ThgStationRow) => void;
}

interface PayoutActionsCellProps extends PayoutRowActions {
  row: ThgStationRow;
}

/**
 * A row's credit-note actions: Generate exactly when the backend allows it (`canGenerate`), and the PDF when
 * a credit note exists. There is no finalise here: drafts are finalised and sent in the accounting system.
 * Every button on the page is disabled while any action runs (research R2); the running one spins.
 */
const PayoutActionsCell: React.FC<PayoutActionsCellProps> = ({ row, busy, locked, onGenerate, onPdf }) => {
  const payout = row.payout;
  // CLR-296: internal stations are never paid, whatever `canGenerate` says.
  if (!payout || isUnpaidInternal(row)) return null;
  const disabled = locked || busy !== null;
  const running = (kind: PayoutBusy["kind"]) => busy?.kind === kind && busy.stationId === row.stationId;
  const hasDocument = payout.creditNote != null && !payout.creating;

  return (
    <div className="flex flex-column align-items-start gap-1">
      {payout.canGenerate && (
        <Button
          label={PAYOUT_COPY.generate}
          icon="pi pi-file-edit"
          size="small"
          outlined
          className="white-space-nowrap"
          loading={running("generate")}
          disabled={disabled}
          onClick={() => onGenerate(row)}
        />
      )}
      {hasDocument && (
        <Button
          label={PAYOUT_COPY.pdf}
          icon="pi pi-file-pdf"
          size="small"
          text
          loading={running("pdf")}
          disabled={disabled}
          onClick={() => onPdf(row)}
        />
      )}
    </div>
  );
};

export default PayoutActionsCell;
